import { HHMMtoDate } from "@/utils/dateTimeUtils";
import { daysOfWeek } from "@/constants/enum";
import {
  getBusinessHoursOfDay,
  getSchedulableDateOptions,
} from "@/utils/scheduleOrderUtils";
import { isEmpty } from "lodash";
import { useTranslations } from "next-intl";
import { PrimaryButton } from "../button/primaryButton";
import ArrowLeftIcon from "@/components/_icons/arrowLeftIcon";
import { ScheduledDateDropdown } from "../scheduledDateDropdown/scheduledDateDropdown";
import { ScheduledTimeDropdown } from "../scheduledTimeDropdown/scheduledTimeDropdown";
import { ScheduleDateType } from "@/dto/scheduledTime";
import { usePathname } from "next/navigation";
import { checkIsHomePage } from "@/utils/nativeBridgeUtils";
import { useEffect } from "react";
import { useNativeActions } from "@/utils/useNativeActions";

export const RestaurantCartScheduledOrderForm = ({
  merchant,
  businessHours,
  handleBackToCart,
  scheduleDate,
  setScheduleDate,
  allDaysEmpty,
  scheduleTime,
  setScheduleTime,
  merchantColor,
  handleConfirmScheduledOrder,
  orderType,
  selectedDayPart = null,
  isOpen,
}: any) => {
  const t = useTranslations();
  const tPickup = useTranslations("CalculatePickTime");
  const pathName = usePathname();
  const isHomePage = checkIsHomePage(
    pathName,
    merchant.merchantMeta.selectedSetup === "Simple",
    merchant.isStandaloneGiftCardWebsite
  );
  const { leaveHomePage, enterHomePage, isCmaApp } = useNativeActions();
  const schedulableDateOptions: ScheduleDateType[] = getSchedulableDateOptions(
    merchant?.scheduledOrdering?.maxDays - 1,
    businessHours,
    merchant?.timezone,
    tPickup,
    merchant.leadTime,
    selectedDayPart
  );
  const getDayPartMenuEndTime = () => {
    const dayIndex = new Date(scheduleDate.dateObject).getDay();
    const dayName = daysOfWeek[dayIndex];
    const businessHoursOfDay = getBusinessHoursOfDay(
      businessHours,
      dayName,
      selectedDayPart
    );
    if (businessHoursOfDay.length > 0) {
      return businessHoursOfDay[0].end;
    }

    return selectedDayPart.time.endTime.toString();
  };

  useEffect(() => {
    if (!isHomePage || !isCmaApp) return;

    if (isOpen) {
      leaveHomePage();
    }

    return () => {
      if (isOpen) {
        enterHomePage();
      }
    };
  }, [isOpen, isHomePage]);

  return (
    <div className="flex flex-col h-full p-4">
      <div className="pt-4 flex flex-row gap-2">
        <button
          onClick={() => {
            handleBackToCart();
          }}
          className="text-gray-600 hover:text-gray-900"
          aria-label={t("checkout.backToCart")}
        >
          <ArrowLeftIcon />
        </button>
        <h2 className="text-xl font-bold">{t("checkout.scheduleYourOrder")}</h2>
      </div>
      {schedulableDateOptions.length > 0 ? (
        <>
          {!isEmpty(selectedDayPart) && (
            <div className="flex flex-row justify-center items-center bg-[#FFF6CC] shadow-md p-2 rounded-md my-4 border-[1px] boder-solid border-[#FFD100]">
              <p>
                {t(
                  "RestaurantCartScheduledOrderForm.daypartScheduleOrderMessage",
                  {
                    dayPartName: selectedDayPart.menuName ?? "",
                    dayPartEndTime:
                      HHMMtoDate(
                        getDayPartMenuEndTime().padStart(4, "0"),
                        merchant.timezone,
                        new Date()
                      )?.toLocaleString({
                        hour: "numeric",
                        minute: "2-digit",
                        hour12: true,
                      }) ?? "",
                  }
                )}
              </p>
            </div>
          )}
          <div className="py-4">
            <ScheduledDateDropdown
              scheduleDate={scheduleDate}
              setScheduleDate={setScheduleDate}
              scheduleDateRange={merchant?.scheduledOrdering?.maxDays - 1}
              setScheduleTime={setScheduleTime}
              businessHours={businessHours}
              timezone={merchant.timezone}
              leadTime={merchant.leadTime}
              selectedDayPart={selectedDayPart}
            />
          </div>
          {businessHours && !allDaysEmpty && (
            <div className="py-4">
              <ScheduledTimeDropdown
                scheduleTime={scheduleTime}
                setScheduleTime={setScheduleTime}
                businessHours={businessHours}
                scheduleDate={scheduleDate}
                leadTime={merchant.leadTime}
                timezone={merchant.timezone}
                orderType={orderType}
                selectedDayPart={selectedDayPart}
              />
            </div>
          )}
          <div className="mt-auto pb-4 flex gap-4">
            <PrimaryButton
              style={{
                backgroundColor:
                  scheduleTime.displayValue == "" ? "#C7C7C7" : merchantColor,
              }}
              className="bg-black text-white font-bold py-2 rounded-md w-full h-14 disabled:bg-[#C7C7C7]"
              onClick={handleConfirmScheduledOrder}
              disabled={scheduleTime.displayValue == ""}
              aria-label={t("RetailCartDrawer.confirm")}
            >
              <p className="text-xl font-medium">
                {t("RetailCartDrawer.confirm")}
              </p>
            </PrimaryButton>
          </div>
        </>
      ) : (
        <p className="pt-4">{t("homePage.noScheduleMsg")}</p>
      )}
    </div>
  );
};
